import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { MessageSquare, Filter, UploadCloud, Tag, MessageCircle, Instagram, Search } from 'lucide-react';
import { Input } from '../../ui/Input';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const ProvasSociais: React.FC = () => {
  const { socialProofs, setSocialProofs } = useStore();
  const [filter, setFilter] = useState('Todos');
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const filters = ['Todos', 'WhatsApp', 'Instagram', 'Google Meu Negócio'];

  const mockPrints = [
    { id: '1', source: 'WhatsApp', text: 'Eu achei que seria muito caro, mas compensou cada centavo! O atendimento foi impecável.', tags: ['Quebra de Objeção: Preço', 'Elogio ao Atendimento'], date: 'Hoje', thumb: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=400&q=80' },
    { id: '2', source: 'Instagram', text: 'Resultado impressionante em apenas 2 semanas. Recomendo muito!', tags: ['Resultado Rápido', 'Recomendação'], date: 'Ontem', thumb: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=400&q=80' },
  ];

  const allPrints = socialProofs.printsAvaliacoes?.length > 0 ? socialProofs.printsAvaliacoes : mockPrints;
  const filteredPrints = filter === 'Todos' ? allPrints : allPrints.filter((p: any) => p.source === filter);

  const handleUpload = (files: File[], title: string) => {
    if (files.length > 0) {
      const file = files[0];
      const newPrint = {
        id: Math.random().toString(36).substr(2, 9),
        source: filter === 'Todos' ? 'WhatsApp' : filter,
        text: title || 'A IA extrairá o texto assim que o print for processado.',
        tags: ['Pendente Análise'],
        date: 'Agora',
        thumb: URL.createObjectURL(file)
      };
      setSocialProofs({ printsAvaliacoes: [newPrint, ...allPrints] });
      toast.success('Print enviado! A IA fará o OCR em breve.');
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <UploadModal 
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Enviar Print/Avaliação"
        accept="image/*"
        onUpload={handleUpload}
      />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Provas Sociais (Prints e Avaliações)</h3>
          <p className="text-sm text-gray-500 mt-1">O &quot;oxigênio&quot; das vendas. Ensine a IA como seus clientes elogiam você.</p>
        </div>
        <button onClick={() => setIsUploadOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <UploadCloud size={16} />
          Fazer Upload de Print
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-4">
        <div className="flex gap-2">
          {filters.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="relative w-64">
           <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
           <input type="text" placeholder="Buscar por tag ou texto..." className="w-full pl-9 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPrints.map((print: any) => (
            <div key={print.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-indigo-300 transition-colors shadow-sm flex gap-5">
               {/* Thumb */}
               <div className="w-24 h-48 bg-gray-100 rounded-lg overflow-hidden shrink-0 relative">
                  <img src={print.thumb} alt="Print" className="w-full h-full object-cover grayscale opacity-50" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-white/80 px-2 py-1 rounded">Print</span>
                  </div>
               </div>
               
               {/* Details */}
               <div className="flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    {print.source === 'WhatsApp' ? <MessageCircle size={14} className="text-green-500" /> : <Instagram size={14} className="text-pink-500" />}
                    <span className="text-xs font-semibold text-gray-700">{print.source}</span>
                    <span className="text-xs text-gray-400 ml-auto">{print.date}</span>
                  </div>

                  {/* Extracted Text (OCR Mock) */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 mb-3 relative">
                     <span className="absolute -top-2 left-3 bg-white text-[9px] font-bold text-indigo-600 px-1 border border-indigo-100 rounded">Texto Extraído (OCR IA)</span>
                     <p className="text-sm text-gray-700 italic mt-1">&quot;{print.text}&quot;</p>
                  </div>

                  {/* Tags */}
                  <div className="mt-auto">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">Tagueamento de Objeção / Benefício</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {print.tags.map((tag: string) => (
                        <span key={tag} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">
                          <Tag size={8} />
                          {tag}
                        </span>
                      ))}
                      <button className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100">
                        + Tag
                      </button>
                    </div>
                  </div>
               </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
